import {ReactNode, Ref, useCallback, useEffect, useRef, useState} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {stopPropagationEvent} from '../../../../../../common/shared/utils/stopPropagationEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import {useAddWindowEventListener} from '../../../../../../common/useAddWindowEventListener.hook';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Loading} from '../../../../../../design-system/atoms/Loading/Loading';
import {PC_VIEWER_HEADER_HEIGHT} from '../../../../../../design-system/organisms/PcHeader/constants';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../../../header/constants';
import {SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME} from '../constant/viewerConstant';
import {useAllFontsLoaded} from '../hook/useAllFontsLoaded.hook';
import {setCurrentChapterId, setCurrentPage} from '../state/textViewerSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {TextViewerFontFamilyType} from '../type/TextViewerFontFamilyType.enum';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {MobileViewerNavMenuContainer} from './MobileViewerNavMenuContainer';
import {PageTextViewerContainer} from './PageTextViewerContainer';
import {PreviewTextViewerContainer} from './PreviewTextViewerContainer';
import {ScrollTextViewerContainer} from './ScrollTextViewerContainer';
import {TextViewerGlobalStyleContainer} from './TextViewerGlobalStyleContainer';
import {TextViewerSeekbarContainer} from './TextViewerSeekbarContainer';

export function TextViewerContainer({
  seriesId,
  productId,
  isCmsPreview,
  isPreview = false,
  viewerRef,
  children,
}: {
  seriesId: number;
  productId: number;
  isCmsPreview: boolean;
  isPreview: boolean;
  viewerRef?: Ref<HTMLDivElement>;
  children?: ReactNode;
}) {
  const [textViewerRootElement, setTextViewerRootElement] =
    useState<HTMLDivElement | null>(null);

  const {isViewerMenuExposed, readType} = useTypedSelector(state => ({
    isViewerMenuExposed: state.viewerMenu.isExposed,
    readType: state.textViewer.settings.readType,
  }));

  const dispatch = useTypedDispatch();

  useEffect(() => {
    if (isPreview) {
      dispatch(setIsViewerMenuExposed({isExposed: false}));
    }
  }, [dispatch, isPreview]);

  const windowClickListener = useCallback(() => {
    dispatch(setIsViewerMenuExposed({isExposed: !isViewerMenuExposed}));
  }, [dispatch, isViewerMenuExposed]);

  useAddWindowEventListener({
    element: textViewerRootElement,
    type: 'click',
    listener: windowClickListener,
  });

  const slideToZero = useCallback(() => {
    dispatch(setCurrentChapterId({productId, chapterId: 0}));
    dispatch(setCurrentPage({productId, page: 1}));
    window.scrollTo(0, 0);
  }, [dispatch, productId]);

  useAddWindowEventListener<any>({
    type: SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME,
    listener: slideToZero,
  });

  const isMobile = requestContext.requestInfo.display.isMobile;

  const isLoaded = useAllFontsLoaded({
    fontList: Object.values(TextViewerFontFamilyType),
    loaded: isPreview ? true : null,
  });

  const scrollViewerRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      ref={isPreview ? viewerRef : setTextViewerRootElement}
      className={twMerge(
        'h-full w-auto',
        !isPreview ? 'absolute w-full' : 'mb-20pxr'
      )}
    >
      {/* 첫 화 보기에서는 시크바 및 뷰어 메뉴를 보여주지 않습니다. */}
      {!isPreview && (
        <div className="relative w-full">
          <div
            className={twMerge(
              'fixed inset-x-0 z-150 pb-safe',
              // 특정 iOS 사파리 브라우저에서 틈새가 발생하는 이슈를 위한 속성입니다. 1px 큰 요소를 만들어 배경으로 씁니다
              isMobile &&
                'before:absolute before:top-0 before:left-0 before:z-[-1] before:h-26pxr before:w-full before:bg-theme-bg-base'
            )}
            style={
              isMobile
                ? {
                    display: !isViewerMenuExposed ? 'none' : undefined,
                    bottom: MOBILE_HEADER_HEIGHT_PX,
                  }
                : {
                    pointerEvents: !isViewerMenuExposed ? 'none' : undefined,
                    top: isViewerMenuExposed ? PC_VIEWER_HEADER_HEIGHT : 0,
                  }
            }
            onClick={stopPropagationEvent}
          >
            <TextViewerSeekbarContainer productId={productId} />
          </div>
          <MobileViewerNavMenuContainer
            seriesId={seriesId}
            productId={productId}
          />
        </div>
      )}
      <TextViewerGlobalStyleContainer
        isCmsPreview={isCmsPreview}
        isPreview={isPreview}
      />
      {isLoaded === null && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
      {isLoaded !== null && (
        <>
          {isPreview && <PreviewTextViewerContainer productId={productId} />}
          {!isPreview && readType === TextViewerReadType.Page && (
            <PageTextViewerContainer
              seriesId={seriesId}
              productId={productId}
              isCmsPreview={isCmsPreview}
            >
              {children}
            </PageTextViewerContainer>
          )}
          {!isPreview && readType === TextViewerReadType.Scroll && (
            <ScrollTextViewerContainer
              seriesId={seriesId}
              productId={productId}
              isCmsPreview={isCmsPreview}
              ref={scrollViewerRef}
              wrapperRef={scrollViewerRef}
            >
              {children}
            </ScrollTextViewerContainer>
          )}
        </>
      )}
    </div>
  );
}
